<template>
  <client-only>
    <header
      id="js-header"
      class="l-header l-header--fixed z-header border-b border-solid border-gray1 transition-[width] duration-300 ease-in-out"
      :class="[
        { 'header--bg-transparent border-none': shouldTextWhite && !useSlideMode },
        { 'is-active border-b !border-solid border-gray1 !bg-white !bg-none': shouldShowHeader },
        { 'l-header--slide-mode': useSlideMode },
        { '!w-[calc(100vw_-_400px)]': isSideOpen },
      ]"
    >
      <div class="l-header__inner">
        <div class="l-header__logo" :class="{ 'is-text-black': !shouldTextWhite }">
          <!-- SP用通貨切り替え: 左端に配置 -->
          <!-- shouldShowHeaderがtrueの時はヘッダーが白背景になるため文字色を暗くする -->
          <div class="l-header__spacer">
            <div class="lg:hidden">
              <CurrencySelectorDrawer ga-location="header_sp" :should-text-white="shouldTextWhite && !shouldShowHeader" />
            </div>
          </div>
          <!-- Center: Logo + Navigation -->
          <div class="l-header__center">
            <nuxt-link to="/">
              <img
                v-if="shouldTextWhite"
                alt="Wabunka logo"
                class="l-header__logo__logoimg"
                height="auto"
                src="@/assets/logo/wabunka/ghost.svg"
                width="auto"
                :class="{ 'should-hide-header-logo': shouldHideLogo }"
              />
              <img v-else alt="Wabunka logo" class="l-header__logo__logoimg" height="auto" src="@/assets/logo/wabunka/default.svg" width="auto" />
              <img alt="Wabunka logo" class="l-header__logo__activelogoimg" height="auto" src="@/assets/logo/wabunka/default.svg" width="auto" />
            </nuxt-link>
            <!-- Navigation Items - 白いヘッダーの時のみ表示 -->
            <nav v-if="shouldShowHeader || !shouldTextWhite" class="header-nav">
              <HeaderNavItem label="Destinations" :is-white-text="shouldTextWhite">
                <HeaderDestinationsDropdown :destinations="destinations" />
              </HeaderNavItem>
              <HeaderNavItem label="Journeys" :is-white-text="shouldTextWhite">
                <HeaderJourneysDropdown :itineraries="itineraries" />
              </HeaderNavItem>
              <HeaderNavItem label="Stays" :is-white-text="shouldTextWhite">
                <HeaderCuratedStaysDropdown :destination-tag-groups="destinationTagGroups" />
              </HeaderNavItem>
              <HeaderNavItem label="Experiences" :is-white-text="shouldTextWhite">
                <HeaderExperiencesDropdown :themes="themes" />
              </HeaderNavItem>
            </nav>
          </div>
          <!-- Right: Menu -->
          <div class="l-header__right">
            <ul class="menu">
              <li class="menu-item group" :class="{ 'is-black': !shouldTextWhite }">
                <button class="menu-item__button" type="button" @click="onOpenSearchModal">
                  <div class="menu-item__button__iconarea search">
                    <img v-if="shouldTextWhite" alt="" class="menu-item__button__icon" height="auto" src="@/assets/icon/search/ghost.svg" width="20" />
                    <img v-else alt="" class="menu-item__button__icon" height="auto" src="@/assets/icon/search/default.svg" width="20" />
                    <img alt="" class="menu-item__button__activeicon" height="auto" src="@/assets/icon/search/default.svg" width="20" />
                  </div>
                  <p class="menu-item__text">Search</p>
                </button>
              </li>
              <li class="menu-item group" :class="{ 'is-black': !shouldTextWhite }">
                <nuxt-link to="/contact/">
                  <div class="menu-item__button">
                    <div class="menu-item__button__iconarea signup">
                      <img v-if="shouldTextWhite" class="menu-item__button__icon" height="auto" src="@/assets/icon/mail/ghost.svg" width="16" />
                      <img v-else class="menu-item__button__icon" height="auto" src="@/assets/icon/mail/default.svg" width="16" />
                      <img class="menu-item__button__activeicon" height="auto" src="@/assets/icon/mail/default.svg" width="16" />
                    </div>
                    <p class="menu-item__text">Contact us</p>
                  </div>
                </nuxt-link>
              </li>
            </ul>
            <div class="l-header__mobile">
              <div class="search-icon-wrapper" @click="onOpenSearchModal">
                <div class="search-icon">
                  <img v-if="shouldTextWhite" alt="" src="@/assets/icon/search/ghost.svg" />
                  <img v-else alt="" src="@/assets/icon/search/default.svg" />
                </div>
              </div>
            </div>
            <!-- PC用通貨切り替え -->
            <!-- shouldShowHeaderがtrueの時はヘッダーが白背景になるため文字色を暗くする -->
            <div class="hidden lg:block">
              <CurrencySelectorDrawer direction="right" ga-location="header_pc" :should-text-white="shouldTextWhite && !shouldShowHeader" />
            </div>
            <!-- ハンバーガーメニュー -->
            <div class="humburger-menu" data-testid="burger-button" @click="onClickMenu">
              <div class="burger-toggle" :class="{ 'is-active': shouldShowGlobalSidebar }">
                <slot>
                  <button class="burger-button" title="Menu" type="button">
                    <span class="burger-bar burger-bar--1"></span>
                    <span class="burger-bar burger-bar--3"></span>
                  </button>
                </slot>
              </div>
            </div>
          </div>
        </div>
      </div>
    </header>
  </client-only>
</template>

<script setup lang="ts">
import { useAsyncWabunkaTags } from '@/composables/models/wabunka/useAsyncWabunkaTags';
import { useGlobalSidebar } from '@/composables/ui/useGlobalSidebar';
import { useSafeWindow } from '@/composables/utils/useSafeWindow';
import { useScrollSensor } from '@/composables/utils/wabunka/useScrollSensor';
import { isHeaderBgTransparentModeForWabunka } from '@/models/ui';
import CurrencySelectorDrawer from '@/wabunka-src/common/components/stateful/CurrencySelectorDrawer/CurrencySelectorDrawer.vue';
import HeaderNavItem from '@/wabunka-src/common/components/stateful/HeaderNavItem/HeaderNavItem.vue';
import HeaderCuratedStaysDropdown from '@/wabunka-src/common/features/Featured/components/HeaderCuratedStaysDropdown/HeaderCuratedStaysDropdown.vue';
import HeaderDestinationsDropdown from '@/wabunka-src/common/features/Featured/components/HeaderDestinationsDropdown/HeaderDestinationsDropdown.vue';
import HeaderExperiencesDropdown from '@/wabunka-src/common/features/Featured/components/HeaderExperiencesDropdown/HeaderExperiencesDropdown.vue';
import HeaderJourneysDropdown from '@/wabunka-src/common/features/Featured/components/HeaderJourneysDropdown/HeaderJourneysDropdown.vue';
import { useNavigationFeatured } from '@/wabunka-src/common/features/Featured/composables/query/useNavigationFeatured';
import { useChatPanel } from '@/wabunka-src/features/Chat/composables/general/useChatPanel';
import { useKeywordSearchModal } from '@/wabunka-src/features/Search/composables/general/useKeywordSearchModal';

const { isSideOpen } = useChatPanel();

const { open } = useKeywordSearchModal();

const onOpenSearchModal = () => {
  open();
};

const EXPERIENCE_DETAIL_ROUTE_NAME = 'experiences-id';
const { destinations, itineraries, themes } = useNavigationFeatured();
const { destinationTagGroups } = await useAsyncWabunkaTags({ throwOnError: false });

const { window: safeWindow } = useSafeWindow();
const { isScrollingUp } = useScrollSensor(safeWindow);

const shouldShowHeader = ref<boolean>(false);
// スクロールでヒーローセクションを過ぎたかどうか（スライドモード判定用）
const isScrolledPastHero = ref<boolean>(false);

const route = useRoute();
const handleScroll = () => {
  // vaulドロワー等でbodyがfixedになっている場合はスクロール処理をスキップ
  if (document.body.style.position === 'fixed') return;
  const scrollTop = document.documentElement.scrollTop || 0;
  if (scrollTop > 50) {
    shouldShowHeader.value = true;
    isScrolledPastHero.value = true;
  }
  if (scrollTop === 0) {
    shouldShowHeader.value = false;
    isScrolledPastHero.value = false;
  }
  // 体験詳細ページのみ、ヘッダー非表示の条件が異なる
  const isWabunkaExperiencePage = route.name === EXPERIENCE_DETAIL_ROUTE_NAME;
  if (isWabunkaExperiencePage && !isScrollingUp.value) {
    shouldShowHeader.value = false;
  }
};

// 体験詳細ページでスクロール済みの場合、スライドモードを使用
const useSlideMode = computed<boolean>(() => {
  const isWabunkaExperiencePage = route.name === EXPERIENCE_DETAIL_ROUTE_NAME;
  return isWabunkaExperiencePage && isScrolledPastHero.value;
});

onMounted(() => {
  if (process.client) {
    window.addEventListener('scroll', handleScroll);
  }
});

onUnmounted(() => {
  if (process.client) {
    window.removeEventListener('scroll', handleScroll);
  }
});

const shouldHideLogo = computed<boolean>(() => {
  if (route.path === '/') {
    return true;
  } else {
    return false;
  }
});

const shouldTextWhite = computed<boolean>(() => {
  // definePageMetaでheaderBgTransparent: trueが設定されている場合はそれを優先
  if (route.meta.headerBgTransparent === true) {
    return true;
  }
  return isHeaderBgTransparentModeForWabunka(route.path);
});

const { isOpen: shouldShowGlobalSidebar, open: showGlobalSidebar } = useGlobalSidebar();

const onClickMenu = () => {
  showGlobalSidebar();
};
</script>

<style lang="scss" scoped>
$text_color: #fff;

button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font: inherit;
  color: inherit;
}

button:focus {
  outline: 0;
}

.l-header {
  position: absolute;
  width: 100vw;
  font-family: 'Noto Sans JP', sans-serif;
  transition:
    background 0.3s cubic-bezier(0.43, 0.05, 0.17, 1),
    border-color 0.3s cubic-bezier(0.43, 0.05, 0.17, 1);
}

.header--bg-transparent {
  background: linear-gradient(180deg, rgba(17, 17, 17, 0.2) 0%, rgba(17, 17, 17, 0) 100%);
}

.l-header--fixed {
  &.is-active {
    position: fixed;
    animation: slideDown 0.3s cubic-bezier(0.43, 0.05, 0.17, 1);
  }
}

@keyframes slideDown {
  0% {
    top: -50px;
  }
  100% {
    top: 0;
  }
}

// 体験詳細ページ用スライドモード: 白背景のまま上からスライドイン/アウト
.l-header--slide-mode {
  position: fixed !important;
  top: -71px; // ヘッダー高さ70px + border 1px
  background: white !important;
  background-image: none !important;
  transition: top 0.3s cubic-bezier(0.43, 0.05, 0.17, 1) !important;
  @media only screen and (max-width: 991px) {
    top: -51px; // ヘッダー高さ50px + border 1px
  }
  &.is-active {
    top: 0;
    animation: none !important;
  }
}

.l-header__inner {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 70px;
  padding: 0 32px;
  @media only screen and (max-width: 991px) {
    height: 50px;
    padding: 0 16px;
  }
}

.l-header__logo {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  a {
    display: flex;
    align-items: center;
    overflow: hidden;
    line-height: 1;
  }
  &__ {
    &logoimg,
    &activelogoimg {
      width: 103px;
      vertical-align: middle;
      @media screen and (min-width: 992px) {
        width: 189px;
      }
    }
    &activelogoimg {
      display: none;
    }
  }
  .should-hide-header-logo {
    display: none;
  }
}

.is-active {
  .l-header__logo__logoimg {
    display: none;
  }
  .l-header__logo__activelogoimg {
    display: block;
  }
  .menu-item {
    &__text {
      color: #333;
    }
    &__button__icon {
      display: none;
    }
    &__button__activeicon {
      display: block;
    }
  }
  .burger-bar {
    background-color: #333;
  }
  .search-icon img {
    content: url('@/assets/icon/search/default.svg');
  }
  .header-nav {
    :deep(.header-nav-item__button) {
      color: #333;
      &:hover,
      &.text-gold1 {
        color: #b8860b;
      }
    }
  }
}

.l-header__spacer {
  width: 50px;
  @media screen and (min-width: 992px) {
    display: none;
  }
}

.l-header__center {
  display: flex;
  align-items: center;
}

.header-nav {
  display: none;
  margin-left: 24px;
  @media screen and (min-width: 992px) {
    display: flex;
    align-items: center;
  }
  :deep(.header-nav-item__button) {
    color: $text_color;
    transition: color 0.5s;
    &:hover,
    &.text-gold1 {
      color: #b8860b;
    }
  }
}

.is-text-black {
  .header-nav {
    :deep(.header-nav-item__button) {
      color: #333;
      &:hover,
      &.text-gold1 {
        color: #b8860b;
      }
    }
  }
}

.l-header__right {
  display: flex;
  align-items: center;
}

.menu {
  display: none;
  @media screen and (min-width: 992px) {
    display: flex;
    align-items: center;
  }
}

.menu-item {
  margin-right: 8px;
  &__button {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 8px;
    cursor: pointer;
  }
  &__button__iconarea {
    margin-right: 6px;
    margin-bottom: 2px;
    background-position: center;
    img {
      transition: all 0.5s;
    }
  }
  &__button__activeicon {
    display: none;
  }
  &__text {
    font-family: 'Spectral', serif;
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: $text_color;
    transition: all 0.5s;
  }
  &.is-black &__text {
    color: #333;
  }
  &:hover {
    .menu-item__text {
      color: #b8860b !important;
    }
    .menu-item__button__iconarea img {
      opacity: 0;
    }
    .search {
      background: url('@/assets/icon/search/gold.svg') no-repeat center;
      background-size: contain;
    }
    .signup {
      background: url('@/assets/icon/mail/gold.svg') no-repeat center;
      background-size: contain;
    }
  }
}

.l-header__mobile {
  display: flex;
  align-items: center;
}

.search-icon-wrapper {
  display: block;
  margin-top: 4px;
  padding: 8px;
  cursor: pointer;
  @media screen and (min-width: 992px) {
    display: none;
  }
}

.search-icon {
  width: 24px;
  height: 24px;
  img {
    width: 100%;
    height: 100%;
  }
}

.is-text-black,
.is-active {
  .burger-bar {
    background-color: #333;
  }
  .search-icon {
    background: url('@/assets/icon/search/default.svg');
    img {
      opacity: 0;
      transition: all 0.4s;
    }
  }
}

.humburger-menu {
  display: block;
}

.burger-button {
  position: relative;
  height: 48px;
  width: 32px;
  display: block;
  z-index: 1;
  border: 0;
  border-radius: 0;
  background-color: transparent;
  pointer-events: all;
  cursor: pointer;
  transition: transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.burger-bar {
  background-color: $text_color;
  position: absolute;
  top: 50%;
  right: 6px;
  left: 6px;
  height: 1px;
  width: 25px;
  margin-top: -1px;
  transition:
    transform 0.4s cubic-bezier(0.165, 0.84, 0.44, 1),
    opacity 0.2s cubic-bezier(0.165, 0.84, 0.44, 1),
    background-color 0.4s cubic-bezier(0.165, 0.84, 0.44, 1);
}

.burger-bar--1 {
  transform: translateY(-4px);
}

.burger-bar--3 {
  transform: translateY(4px);
}

.burger-toggle.is-active .burger-button {
  transform: rotate(-180deg);
}

.burger-toggle.is-active .burger-bar {
  background-color: #333;
}

.burger-toggle.is-active .burger-bar--1 {
  transform: rotate(45deg);
}

.burger-toggle.is-active .burger-bar--3 {
  transform: rotate(-45deg);
}

a {
  color: $text_color;
  text-decoration: none;
}

@media only screen and (max-width: 991px) {
  .l-header__logo {
    height: 100%;
    margin-right: 0;
    .menu {
      display: none;
    }
  }
}
</style>
